import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Chargement } from '../components/Elements';
import { formaterDate, formaterHeure, formaterMontant, libelleJour } from '../lib/format';
import type { CataloguePublic, CreneauPropose } from '../types';
import { ChampTelephone } from '../components/ChampTelephone';

/**
 * INSCRIPTION EN LIGNE — la page PUBLIQUE (2026-09-24).
 *
 * Reservee aux adultes des chorales et de l'orchestre, sans compte ni
 * session. Elle reproduit la fiche papier de l'ecole dans le meme ordre,
 * et ne parle a l'API que par deux routes publiques : le catalogue des
 * creneaux proposes, et le depot d'une demande. Rien de ce que l'ecole
 * sait n'en sort.
 *
 * Elle vit HORS du bandeau : pas de AppLayout, pas de FournisseurAnnee.
 * Le logo est celui d'Artistic, en fichier statique — la route du logo
 * d'entite exige une session (RG-30).
 */

const PUPITRES = [
  { valeur: 'S', libelle: 'Soprano' },
  { valeur: 'A', libelle: 'Alto' },
  { valeur: 'T', libelle: 'Ténor' },
  { valeur: 'B', libelle: 'Basse' },
] as const;

/** La forme d'une adresse email : quelque chose, un @, un domaine avec un point. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Bloque copier, couper, coller ou deposer sur un champ. */
const bloquer = (evenement: { preventDefault: () => void }) => evenement.preventDefault();

function ageRevolu(dateNaissance: string): number {
  const naissance = new Date(dateNaissance);
  const aujourdHui = new Date();
  let age = aujourdHui.getFullYear() - naissance.getFullYear();
  const m = aujourdHui.getMonth() - naissance.getMonth();
  if (m < 0 || (m === 0 && aujourdHui.getDate() < naissance.getDate())) age -= 1;
  return age;
}

export function InscriptionEnLignePage() {
  const { code = 'ARTISTIC' } = useParams();
  const catalogue = useRessource(
    () => api.get<CataloguePublic>(`/api/public/inscription/${encodeURIComponent(code)}`),
    [code],
  );
  const [confirmation, setConfirmation] = useState<{ numero: string; creneau: string } | null>(null);

  const donnees = catalogue.donnees;

  return (
    <div className="min-h-screen bg-encre-50 px-4 py-8">
      <div className="mx-auto max-w-2xl space-y-4">
        <img src="/assets/logo-artistic.jpg" alt="Artistic, école de musique" className="mx-auto w-48" />

        {catalogue.chargement && <Chargement />}
        {catalogue.erreur && (
          <p className="carte p-4 text-sm text-red-700">
            Cette page est indisponible pour le moment. Réessayez plus tard, ou écrivez à l’école.
          </p>
        )}

        {donnees && confirmation && (
          <Confirmation donnees={donnees} confirmation={confirmation} />
        )}

        {donnees && !confirmation && !donnees.ouverte && <Fermee donnees={donnees} />}

        {donnees && !confirmation && donnees.ouverte && (
          <Formulaire code={code} donnees={donnees} onEnvoye={setConfirmation} />
        )}

        <PiedDePage />
      </div>
    </div>
  );
}

function Fermee({ donnees }: { donnees: CataloguePublic }) {
  return (
    <div className="carte space-y-2 p-6">
      <h1 className="text-xl font-semibold">Inscriptions en ligne fermées</h1>
      <p className="text-sm text-encre-600">
        {donnees.fenetre.debut && donnees.fenetre.fin
          ? `Le formulaire est ouvert du ${formaterDate(donnees.fenetre.debut)} au ${formaterDate(donnees.fenetre.fin)}.`
          : 'Le formulaire n’est pas ouvert pour le moment.'}{' '}
        Pour vous inscrire, écrivez à l’école ou passez au secrétariat.
      </p>
      {donnees.message && <p className="text-sm text-encre-600">{donnees.message}</p>}
    </div>
  );
}

function Confirmation({
  donnees,
  confirmation,
}: {
  donnees: CataloguePublic;
  confirmation: { numero: string; creneau: string };
}) {
  return (
    <div className="carte space-y-3 p-6">
      <h1 className="text-xl font-semibold">Demande envoyée</h1>
      <p className="text-sm">
        Votre demande pour <strong>{confirmation.creneau}</strong> a bien été reçue par{' '}
        {donnees.entite.raison_sociale}.
      </p>
      <div>
        <div className="etiquette">Numéro de demande</div>
        <div className="tabulaire text-2xl font-semibold tracking-wide">{confirmation.numero}</div>
      </div>
      <p className="text-sm text-encre-500">
        Notez-le : il vous sera demandé si vous contactez l’école. Le secrétariat vous confirme votre
        place par email, puis vous indique comment régler.
      </p>
      <p className="text-xs text-encre-400">
        Une erreur dans votre saisie ? Écrivez à l’école en rappelant ce numéro.
      </p>
    </div>
  );
}

function Formulaire({
  code,
  donnees,
  onEnvoye,
}: {
  code: string;
  donnees: CataloguePublic;
  onEnvoye: (confirmation: { numero: string; creneau: string }) => void;
}) {
  const premierLibre = donnees.creneaux.find((c) => !c.complet);
  const [valeurs, setValeurs] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    email: '',
    emailConfirmation: '',
    telephone: '',
    telephonePays: 'FR',
    voie: '',
    codePostal: '',
    ville: '',
    creneauId: premierLibre?.id ?? '',
    pupitre: '',
    instrument: '',
    facture: false,
    facturePrecisions: '',
    contribution: '',
    // Non par defaut : la fiche papier ne pre-remplit rien, et c'est la
    // personne qui adhere.
    adhesion: false,
    charteAcceptee: false,
    // Le piege a robots : un humain ne le voit pas, un script le remplit.
    site: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const poser = <K extends keyof typeof valeurs>(cle: K, valeur: (typeof valeurs)[K]) =>
    setValeurs((v) => ({ ...v, [cle]: valeur }));

  const creneau: CreneauPropose | undefined = donnees.creneaux.find((c) => c.id === valeurs.creneauId);
  const adhesion = donnees.montant_adhesion ?? 0;
  const total = (creneau?.tarif ?? 0) + (valeurs.adhesion ? adhesion : 0);
  // « la chorale » ou « l'orchestre », selon le creneau choisi.
  const ensemble = creneau && !creneau.chorale ? 'l’orchestre' : 'la chorale';

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    if (!creneau) {
      setErreur('Choisissez votre chorale ou orchestre.');
      return;
    }
    if (valeurs.dateNaissance && ageRevolu(valeurs.dateNaissance) < 18) {
      setErreur(
        'Les inscriptions en ligne sont réservées aux adultes. Pour un mineur, contactez l’école.',
      );
      return;
    }
    if (!EMAIL.test(valeurs.email)) {
      setErreur('L’adresse email n’a pas une forme valide.');
      return;
    }
    if (valeurs.email.trim().toLowerCase() !== valeurs.emailConfirmation.trim().toLowerCase()) {
      setErreur('Les deux adresses email ne sont pas identiques.');
      return;
    }
    if (valeurs.codePostal && !/^[0-9]{5}$/.test(valeurs.codePostal)) {
      setErreur('Le code postal doit comporter 5 chiffres.');
      return;
    }
    if (!valeurs.charteAcceptee) {
      setErreur('Merci d’accepter la charte du choriste et les statuts de l’association.');
      return;
    }
    setEnCours(true);
    try {
      const reponse = await api.post<{ numero: string; creneau: string }>(
        `/api/public/inscription/${encodeURIComponent(code)}`,
        {
          nom: valeurs.nom,
          prenom: valeurs.prenom,
          dateNaissance: valeurs.dateNaissance || null,
          email: valeurs.email.trim(),
          emailConfirmation: valeurs.emailConfirmation.trim(),
          telephone: valeurs.telephone,
          telephonePays: valeurs.telephonePays,
          adresse: {
            voie: valeurs.voie || null,
            codePostal: valeurs.codePostal || null,
            ville: valeurs.ville || null,
          },
          creneauId: valeurs.creneauId,
          pupitre: creneau.chorale && valeurs.pupitre ? valeurs.pupitre : null,
          instrument: !creneau.chorale && valeurs.instrument ? valeurs.instrument : null,
          adhesion: valeurs.adhesion,
          facture: valeurs.facture,
          facturePrecisions: valeurs.facturePrecisions || null,
          contribution: valeurs.contribution || null,
          charteAcceptee: true,
          site: valeurs.site,
        },
      );
      onEnvoye(reponse);
      window.scrollTo(0, 0);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Envoi impossible. Réessayez.');
    } finally {
      setEnCours(false);
    }
  };

  const bloc = 'space-y-3 p-5';
  const titreBloc = 'text-xs font-semibold uppercase tracking-wide text-encre-500';

  return (
    <form onSubmit={soumettre} className="space-y-4">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">
          Inscription chorale et orchestre {donnees.annee ? donnees.annee.replace('-', '‑20') : ''}
        </h1>
        <p className="mx-auto mt-1 max-w-md text-sm text-encre-500">
          {donnees.message ??
            'Le secrétariat confirme votre inscription par email sous quelques jours. Le règlement se fait ensuite, à l’école.'}
        </p>
      </div>

      <div className="carte divide-y divide-encre-100">
        <section className={bloc}>
          <h2 className={titreBloc}>Vous</h2>
          {/* Pas de civilite : le serveur la deduit du prenom, et la
              laisse vide en cas de doute. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="etiquette">Nom</span>
              <input className="champ" required value={valeurs.nom} onChange={(e) => poser('nom', e.target.value)} autoComplete="family-name" />
            </label>
            <label className="block">
              <span className="etiquette">Prénom</span>
              <input className="champ" required value={valeurs.prenom} onChange={(e) => poser('prenom', e.target.value)} autoComplete="given-name" />
            </label>
          </div>
          <label className="block sm:w-1/2">
            <span className="etiquette">Date de naissance <span className="font-normal text-encre-400">facultatif</span></span>
            <input className="champ" type="date" value={valeurs.dateNaissance} onChange={(e) => poser('dateNaissance', e.target.value)} autoComplete="bday" />
            <span className="mt-1 block text-xs text-encre-400">Les inscriptions en ligne sont réservées aux adultes.</span>
          </label>
        </section>

        <section className={bloc}>
          <h2 className={titreBloc}>Vos coordonnées</h2>
          {/* L'EMAIL EST LA CLE de cette page : une adresse, une demande.
              Il se saisit deux fois, et le copier-coller entre les deux
              champs est bloque — sinon la seconde saisie ne verifie rien. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="etiquette">Email</span>
              <input
                className="champ"
                type="email"
                required
                value={valeurs.email}
                onChange={(e) => poser('email', e.target.value)}
                onCopy={bloquer}
                onCut={bloquer}
                autoComplete="email"
              />
              {valeurs.email && !EMAIL.test(valeurs.email) && (
                <span className="mt-1 block text-xs text-red-700">Adresse incomplète ou invalide.</span>
              )}
            </label>
            <label className="block">
              <span className="etiquette">Confirmez votre email</span>
              <input
                className="champ"
                type="email"
                required
                value={valeurs.emailConfirmation}
                onChange={(e) => poser('emailConfirmation', e.target.value)}
                onPaste={bloquer}
                onDrop={bloquer}
                autoComplete="off"
              />
              {valeurs.emailConfirmation &&
                valeurs.emailConfirmation.trim().toLowerCase() !== valeurs.email.trim().toLowerCase() && (
                  <span className="mt-1 block text-xs text-red-700">Les deux adresses ne sont pas identiques.</span>
                )}
            </label>
          </div>
          <div className="sm:w-1/2">
            <ChampTelephone
              numero={valeurs.telephone}
              pays={valeurs.telephonePays}
              required
              autoComplete="tel"
              onChange={(telephone, telephonePays) =>
                setValeurs((v) => ({ ...v, telephone, telephonePays }))
              }
            />
          </div>
          <label className="block">
            <span className="etiquette">Adresse <span className="font-normal text-encre-400">facultatif</span></span>
            <input className="champ" value={valeurs.voie} onChange={(e) => poser('voie', e.target.value)} autoComplete="street-address" />
          </label>
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
            <label className="block">
              <span className="etiquette">Code postal</span>
              <input className="champ tabulaire" inputMode="numeric" pattern="[0-9]{5}" value={valeurs.codePostal} onChange={(e) => poser('codePostal', e.target.value)} autoComplete="postal-code" />
            </label>
            <label className="block">
              <span className="etiquette">Ville</span>
              <input className="champ" value={valeurs.ville} onChange={(e) => poser('ville', e.target.value)} autoComplete="address-level2" />
            </label>
          </div>
        </section>

        <section className={bloc}>
          <h2 className={titreBloc}>Votre chorale ou orchestre</h2>
          {donnees.creneaux.length === 0 ? (
            <p className="text-sm text-encre-500">Aucun créneau n’est proposé pour le moment.</p>
          ) : (
            <div className="space-y-2" role="radiogroup" aria-label="Créneau">
              {donnees.creneaux.map((c) => {
                const retenu = c.id === valeurs.creneauId;
                return (
                  <label
                    key={c.id}
                    className={`grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border px-3 py-2.5 ${
                      c.complet
                        ? 'cursor-not-allowed border-encre-200 text-encre-400'
                        : retenu
                          ? 'border-encre-900 ring-1 ring-encre-900'
                          : 'border-encre-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="creneau"
                      value={c.id}
                      disabled={c.complet}
                      checked={retenu}
                      onChange={() => poser('creneauId', c.id)}
                    />
                    <span>
                      <span className="font-medium">{c.activite}</span>
                      <br />
                      <span className="text-xs text-encre-500">
                        {libelleJour(c.jour)} {formaterHeure(c.heure_debut)} – {formaterHeure(c.heure_fin)}
                        {c.premiere_seance ? ` · dès le ${formaterDate(c.premiere_seance)}` : ''}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="tabulaire font-medium">
                        {c.tarif !== null ? formaterMontant(c.tarif) : ''}
                      </span>
                      <br />
                      <span className={`tabulaire text-[11px] ${c.complet ? 'text-red-700' : 'text-emerald-700'}`}>
                        {c.complet
                          ? 'complet'
                          : c.places_restantes === null
                            ? 'places disponibles'
                            : `${c.places_restantes} place${c.places_restantes > 1 ? 's' : ''} disponible${c.places_restantes > 1 ? 's' : ''}`}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {/* Le pupitre n'existe que pour une chorale, l'instrument que
              pour l'orchestre : le champ sans objet n'apparait pas. */}
          {creneau?.chorale && (
            <label className="block sm:w-1/2">
              <span className="etiquette">Pupitre</span>
              <select className="champ" value={valeurs.pupitre} onChange={(e) => poser('pupitre', e.target.value)}>
                <option value="">Je ne sais pas encore</option>
                {PUPITRES.map((p) => (
                  <option key={p.valeur} value={p.valeur}>
                    {p.libelle}
                  </option>
                ))}
              </select>
            </label>
          )}
          {creneau && !creneau.chorale && (
            <label className="block sm:w-1/2">
              <span className="etiquette">Instrument</span>
              <input
                className="champ"
                value={valeurs.instrument}
                placeholder="Accordéon, clarinette, violon…"
                onChange={(e) => poser('instrument', e.target.value)}
              />
            </label>
          )}
        </section>

        <section className={bloc}>
          <h2 className={titreBloc}>Adhésion et facture</h2>
          <div>
            <span className="etiquette">
              Adhésion à l’association, {formaterMontant(adhesion)}{' '}
              <span className="font-normal text-encre-400">facultative</span>
            </span>
            <div className="mt-1 flex gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="adhesion" checked={valeurs.adhesion} onChange={() => poser('adhesion', true)} /> Oui
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="adhesion" checked={!valeurs.adhesion} onChange={() => poser('adhesion', false)} /> Non
              </label>
            </div>
            <span className="mt-1 block text-xs text-encre-400">
              L’adhésion donne voix à l’assemblée générale de l’association.
            </span>
          </div>
          <label className="inline-flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={valeurs.facture} onChange={(e) => poser('facture', e.target.checked)} />
            <span>Je souhaite une facture</span>
          </label>
          {valeurs.facture && (
            <label className="block">
              <span className="etiquette">Précisions pour la facture <span className="font-normal text-encre-400">facultatif</span></span>
              <input className="champ" value={valeurs.facturePrecisions} placeholder="Nom de l’organisme, adresse de facturation…" onChange={(e) => poser('facturePrecisions', e.target.value)} />
            </label>
          )}
          <div className="flex items-baseline justify-between border-t border-dashed border-encre-200 pt-3">
            <span className="text-xs text-encre-500">
              {creneau ? `${creneau.activite} ${creneau.tarif !== null ? formaterMontant(creneau.tarif) : ''}` : ''}
              {valeurs.adhesion ? ` + adhésion ${formaterMontant(adhesion)}` : ''}
            </span>
            <span className="tabulaire text-lg font-semibold">{formaterMontant(total)}</span>
          </div>
          <p className="text-xs text-encre-500">
            Si vous bénéficiez d’un tarif réduit, il sera appliqué par le secrétariat à la
            validation de votre inscription.
          </p>
        </section>

        {/* Toujours affiche : le titre et la question suivent le creneau
            choisi — la chorale ou l'orchestre. */}
        <section className={bloc}>
          <h2 className={titreBloc}>La vie de {ensemble}</h2>
          <label className="block">
            <span className="etiquette">
              En quoi vos hobbies, votre savoir‑faire ou simplement votre motivation peuvent contribuer à la vie de {ensemble} ?
            </span>
            <textarea className="champ min-h-[5rem]" value={valeurs.contribution} onChange={(e) => poser('contribution', e.target.value)} />
            <span className="mt-1 block text-xs text-encre-400">
              Réseaux sociaux, week‑end de résidence, organisation des concerts, photos, montage vidéo…
            </span>
          </label>
        </section>

        <section className={bloc}>
          <label className="inline-flex items-start gap-2 text-sm">
            {/* Obligatoire, et tenu trois fois : ici par le navigateur, dans
                la soumission par le message, et au serveur par le schema. */}
            <input type="checkbox" className="mt-0.5" required checked={valeurs.charteAcceptee} onChange={(e) => poser('charteAcceptee', e.target.checked)} />
            <span>
              J’ai lu et j’accepte{' '}
              {donnees.url_charte ? (
                <a className="underline" href={donnees.url_charte} target="_blank" rel="noopener noreferrer">la charte du choriste</a>
              ) : (
                'la charte du choriste'
              )}{' '}
              et{' '}
              {donnees.url_statuts ? (
                <a className="underline" href={donnees.url_statuts} target="_blank" rel="noopener noreferrer">les statuts de l’association</a>
              ) : (
                'les statuts de l’association'
              )}
              .
            </span>
          </label>

          {/* Le champ piege : hors ecran, hors tabulation, sans completion. */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <label>
              Site web
              <input tabIndex={-1} autoComplete="off" value={valeurs.site} onChange={(e) => poser('site', e.target.value)} />
            </label>
          </div>

          {erreur && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{erreur}</p>}

          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="min-w-[15rem] flex-1 text-xs text-encre-400">
              Vos données servent à traiter votre inscription et à vous contacter. Elles sont
              conservées le temps de votre adhésion.
            </span>
            <button type="submit" className="bouton-principal" disabled={enCours}>
              {enCours ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          </div>
        </section>
      </div>
    </form>
  );
}

function PiedDePage() {
  return (
    <p className="text-center text-[11px] leading-relaxed text-encre-400">
      École de Musique Artistic · 183 rue Saint‑Denis 75002 Paris · info@artistic-online.com · 06 60 69 33 76
      <br />
      Agrément Jeunesse Éducation Populaire n° 75 JEP 11‑9 · Siret 483 263 679 00035
    </p>
  );
}
